/* eslint-disable react/no-danger */
/* eslint-disable react/no-children-prop */
import type { FC } from 'react';
import React, { memo, useState, useContext } from 'react';
import { useIntl } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { SpotlightManager } from '@atlaskit/onboarding';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import Blanket from '@atlaskit/blanket';
import { Panel } from '@atlaskit/navigation-system/layout/panel';
import { Banner } from '@atlaskit/navigation-system/layout/banner';
import { Main } from '@atlaskit/navigation-system/layout/main';
import { Root } from '@atlaskit/navigation-system/layout/root';

import { EditContentButtonPreloaderContext } from '@confluence/edit-button/entry-points/EditContentButtonPreloaderContext';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { PERFORMANCE_SUBJECT_navigation } from '@confluence/performance/entry-points/constants';
import { PerformanceStart } from '@confluence/performance/entry-points/PerformanceStart';
import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { BannerContainer } from '@confluence/banner-container';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer';
import { FixedBreadcrumbsNavForPageLayoutLoader } from '@confluence/breadcrumbs/entry-points/Breadcrumbs';
import {
	CUSTOM_CONTENTS_ON_PAGE_LIST,
	SPACE_PAGES,
	SPACE_CALENDARS,
	DATABASE_CUSTOM_OVERVIEW,
	SPACE_QUESTIONS,
} from '@confluence/named-routes/entry-points/routes';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import type { Route } from '@confluence/route/entry-points/Route';
import {
	perfMark,
	WATERFALL_INITIAL_MEASURES,
} from '@confluence/action-measures/entry-points/WaterfallMeasures';
import { GlobalComponentsLoader } from '@confluence/global-components';
import { PreviewPanel } from '@confluence/preview-panel-api/entry-points/PreviewPanelContainer';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { useMoreActionsObjectSidebarTourState } from '@confluence/object-sidebar-components/entry-points/useMoreActionsObjectSidebarTourState';
import { useModernizeChangeboardingActive } from '@confluence/onboarding-helpers/entry-points/hooks/useModernizeChangeboardingActive';
import { useRedactionsTourIsActive } from '@confluence/redactions/entry-points/tour';
import { useEnhanceSpotlightTourActive } from '@confluence/experiment-enhance-orientation-tour/entry-points/useEnhanceSpotlightTourActive';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive';

import { NavigationExperienceWrapper } from './NavigationExperienceWrapper';
import { SideNavigation } from './SideNavigation';
import { useScrollTree } from './useScrollTree';
import { PanelLoader } from './PanelLoader';
import { i18n } from './messages';
import type { PageLayoutProps } from './PageLayoutTypes';
import {
	ContentRightSidePanel,
	LayoutMainWrapper,
	globalStylesCompiled,
	fixSSRContentForNewLayoutWithRightSidebarStyles,
	fixSSRContentForNewLayoutWithRightSidebarStylesNew,
	SSR_LAYOUT_DATA_ATTR,
	SSR_IFRAME_ATTR,
} from './presentationComponents';
import { useSideNavInitialState } from './useSideNavInitialState';
import { TopNav } from './TopNav';
import { PageLayoutContextProviderWrapper } from './PageLayoutContextProviderWrapper';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher';
import { useKeyboardShortcutsEnabled } from '@confluence/shortcuts/entry-points/useKeyboardShortcutsEnabled';

const TOP_NAV_HEIGHT = 48;

const isRoute = (pathname: string | undefined, route: Route) =>
	Boolean(route.match(pathname || ''));

const routesToShowPageLayoutBreadcrumbNav = [
	CUSTOM_CONTENTS_ON_PAGE_LIST,
	SPACE_PAGES,
	SPACE_CALENDARS,
	DATABASE_CUSTOM_OVERVIEW,
	SPACE_QUESTIONS,
];

export const PageLayout: FC<PageLayoutProps> = memo(
	({ children, view, standalone, isSpaceSettingsScreen, editorPreloader }) => {
		return (
			<ErrorBoundary
				attribution={Attribution.BACKBONE}
				attributes={{
					errorBoundaryId: 'PageLayout-outer',
				}}
			>
				<PageLayoutInner
					children={children}
					view={view}
					standalone={standalone}
					isSpaceSettingsScreen={isSpaceSettingsScreen}
					editorPreloader={editorPreloader}
				/>
			</ErrorBoundary>
		);
	},
);

export const PageLayoutInner: FC<PageLayoutProps> = ({
	children,
	view,
	standalone,
	isSpaceSettingsScreen = false,
	editorPreloader = noopEditorPreloader,
}) => {
	const { pageTreeUpdater } = useScrollTree({ view });
	const [areKeyboardShortcutsEnabled] = useKeyboardShortcutsEnabled();

	const spotlightTriggeredByJswQS = false;
	const isQuickStartOpen = false;

	const intl = useIntl();
	const { location } = useContext(RoutesContext);

	// show breadcrumb nav for routes listed in routesToShowPageLayoutBreadcrumbNav
	const showBreadcrumbNavHeader = routesToShowPageLayoutBreadcrumbNav.some((route) =>
		isRoute(location?.pathname, route),
	);

	const SideNavSkiplinkText = intl.formatMessage(i18n.SideNav);

	const nonceValue = window.__SSR_NONCE_VALUE__;

	const standaloneGlobalStylesCompiled = (bannerHeight: number) =>
		standalone ? (
			/*
			 * We're injecting global styles to handle styling targets that are not part of the React app, but are children of the HTML body
			 * Previously, this used to be a `<Global styles={[…]} />` from Emotion
			 * But in the Compiled migration, there is no alternative and feels like the best path forward for now.
			 */
			<>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles */}
				<style
					type="text/css"
					dangerouslySetInnerHTML={{
						__html: fg('confluence_nav4_layout_bug_fixes_1')
							? fixSSRContentForNewLayoutWithRightSidebarStylesNew
							: fixSSRContentForNewLayoutWithRightSidebarStyles,
					}}
					data-testid="global-styles-SSR"
				/>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles */}
				<style
					{...(nonceValue !== undefined ? { nonce: nonceValue } : {})}
					type="text/css"
					dangerouslySetInnerHTML={{ __html: globalStylesCompiled }}
				/>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles */}
				<style type="text/css">{`[${SSR_LAYOUT_DATA_ATTR}], [${SSR_IFRAME_ATTR}] {
					top: ${bannerHeight + TOP_NAV_HEIGHT + (fg('confluence_nav4_layout_bug_fixes_1') ? 0 : 2)}px !important;
				}`}</style>
			</>
		) : (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles
			<style
				data-testid="global-styles"
				{...(nonceValue !== undefined ? { nonce: nonceValue } : {})}
				type="text/css"
				dangerouslySetInnerHTML={{ __html: globalStylesCompiled }}
			/>
		);

	const [isNav4CollapseReminderSpotlightActive, setIsNav4CollapseReminderSpotlightActive] =
		useState<boolean>(false);
	const { initialCollapsedState } = useSideNavInitialState();
	const [{ activeSpotlightIndex: moreActionsActiveSpotlightIndex }] =
		useMoreActionsObjectSidebarTourState();

	const isModernizeChangeboardingActive = useModernizeChangeboardingActive();
	const isEnhanceSpotlightTourActive = useEnhanceSpotlightTourActive();
	const isRedactionsTourActive = useRedactionsTourIsActive();

	return (
		<ErrorBoundary
			attribution={Attribution.BACKBONE}
			attributes={{
				errorBoundaryId: 'PageLayoutInner',
			}}
		>
			<NavigationExperienceWrapper view={view} navVersion="4">
				<PerformanceStart subject={PERFORMANCE_SUBJECT_navigation} subjectId="NavigationLoading" />
				<EditContentButtonPreloaderContext.Provider value={editorPreloader}>
					<Subscribe to={[BannerStateContainer]}>
						{(bannerState: BannerStateContainer) => {
							const bannerHeight = bannerState.getTotalHeight(false);

							return (
								<SpotlightManager
									blanketIsTinted={
										spotlightTriggeredByJswQS ||
										moreActionsActiveSpotlightIndex !== null ||
										isRedactionsTourActive ||
										isModernizeChangeboardingActive ||
										isNav4CollapseReminderSpotlightActive ||
										isEnhanceSpotlightTourActive ||
										fg('remove_conditional_blanket_tint')
									}
								>
									{standaloneGlobalStylesCompiled(bannerHeight)}
									<PageLayoutContextProviderWrapper>
										<Root
											UNSAFE_dangerouslyHoistSlotSizes
											testId="page-layout-root"
											defaultSideNavCollapsed={
												fg('navx-full-height-sidebar')
													? initialCollapsedState === 'collapsed'
													: undefined
											}
											isSideNavShortcutEnabled={
												areKeyboardShortcutsEnabled && fg('navx-full-height-sidebar')
											}
										>
											{/*<ZIndexWrapper zIndex={14}>*/}
											<Banner height={bannerHeight} id="AkBanner">
												<BannerContainer />
											</Banner>
											<SuspenseWithDataFetcher dataFetcherName="top-nav">
												{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_TOP_NAV}.start`)}
												<TopNav defaultCollapsed={initialCollapsedState === 'collapsed'} />
												{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_TOP_NAV}.end`)}
											</SuspenseWithDataFetcher>

											{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_SIDE_NAV}.start`)}
											<SideNavigation
												view={view}
												skiplinkText={SideNavSkiplinkText}
												isSpaceSettingsScreen={isSpaceSettingsScreen}
												isNav4CollapseReminderSpotlightActive={
													isNav4CollapseReminderSpotlightActive
												}
												setIsNav4CollapseReminderSpotlightActive={
													setIsNav4CollapseReminderSpotlightActive
												}
											/>
											{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_SIDE_NAV}.end`)}
											{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_MAIN}.start`)}
											<Main testId="grid-main-container" id="AkMainContent">
												{expVal('confluence_bulk_actions_in_content_tree', 'isEnabled', false) && (
													<ContentTreeMultiSelectOverlay />
												)}
												<LayoutMainWrapper
													id="layout-main-wrapper"
													showBreadcrumbNavHeader={showBreadcrumbNavHeader}
												>
													{showBreadcrumbNavHeader && <FixedBreadcrumbsNavForPageLayoutLoader />}

													{children && !standalone ? (
														<ErrorBoundary attribution={Attribution.DISCO}>
															{showBreadcrumbNavHeader ? (
																<Box xcss={breadcrumbsOffsetStyles}>{children}</Box>
															) : (
																children
															)}
														</ErrorBoundary>
													) : null}
													{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_MAIN}.end`)}
												</LayoutMainWrapper>
											</Main>
											{isQuickStartOpen && (
												<Panel>
													<ContentRightSidePanel id="content-right-side-panel" />
												</Panel>
											)}
											<PanelLoader toggleInProductHelp={() => {}} />
											<Subscribe to={[PageTreeStateUpdater]}>
												{(pageTreeStateUpdatesContainer: PageTreeStateUpdater) => {
													pageTreeUpdater.current = pageTreeStateUpdatesContainer;
													return null;
												}}
											</Subscribe>
											<PreviewPanel />
										</Root>
										<GlobalComponentsLoader />
									</PageLayoutContextProviderWrapper>
								</SpotlightManager>
							);
						}}
					</Subscribe>
				</EditContentButtonPreloaderContext.Provider>
			</NavigationExperienceWrapper>
		</ErrorBoundary>
	);
};

const ContentTreeMultiSelectOverlay = () => {
	const [isMultiSelectActive] = useIsMultiSelectActive();
	return isMultiSelectActive ? (
		<Blanket isTinted testId="content-tree-multi-select-blanket" />
	) : null;
};

const breadcrumbsOffsetStyles = xcss({
	// @ts-ignore This specific value is necessary to ensure the breadcrumbs are offset correctly
	marginTop: '60px',
});

const noopEditorPreloader: NonNullable<PageLayoutProps['editorPreloader']> = () => null;
